import React, { useState, useMemo } from 'react';
import { useStickyFilter } from '../hooks/useStickyFilter';
import { 
  CheckSquare,
  User,
  Clock,
  MessageCircle,
  ChevronDown,
  ChevronUp,
  ExternalLink,
  Calendar,
  AlertTriangle,
  CheckCircle2,
  Users
} from 'lucide-react';

const PCTasksChecklist = ({ tasks, currentUser, loading, error }) => {
  const [searchTerm, setSearchTerm] = useState('');
  // Search / filter card stays pinned while the list scrolls under it.
  const [filterSentinelRef, isFilterStuck] = useStickyFilter();
  const [expandedDoers, setExpandedDoers] = useState({});

  // Filter tasks to only include CheckList Task items
  const checklistTasks = useMemo(() => {
    if (!Array.isArray(tasks)) return [];
    
    return tasks.filter(task => {
      const fmsType = task.fms || '';
      return fmsType.toLowerCase().includes('checklist task');
    });
  }, [tasks]);

  // Helper functions (based on PCTasks component structure)
  const getTaskInfo = (task) => {
    return task.task_info || task.taskinfo || task.what_to_do || '';
  };

  const getWhatToDo = (task) => {
    return task.what_to_do || task.whattodo || '';
  };

  const getDoer = (task) => {
    return task.doer || task.assignedTo || '';
  };

  const getDeadline = (task) => {
    return task.deadline || task.due_date || task.duedate || '';
  };

  const getDelay = (task) => {
    return task.delay || '';
  };

  const getLink = (task) => {
    return task.link || '';
  };

  const isTaskOverdue = (task) => {
  const delay = parseFloat(task.delay || 0);
  return delay > 0; // Any positive number means overdue
};

  // Filter and group tasks by doer
  const groupedChecklistTasks = useMemo(() => {
    let filteredTasks = checklistTasks;

    // Apply search filter
    if (searchTerm) {
      filteredTasks = filteredTasks.filter(task =>
        getTaskInfo(task).toLowerCase().includes(searchTerm.toLowerCase()) ||
        getWhatToDo(task).toLowerCase().includes(searchTerm.toLowerCase()) ||
        getDoer(task).toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Group by doer
    const grouped = {};
    filteredTasks.forEach(task => {
      const doer = getDoer(task) || 'Unassigned';
      if (!grouped[doer]) {
        grouped[doer] = [];
      }
      grouped[doer].push(task);
    });

    // Sort doers alphabetically
    const sortedGrouped = {};
    Object.keys(grouped).sort((a, b) => a.localeCompare(b)).forEach(doer => {
      sortedGrouped[doer] = grouped[doer];
    });

    return sortedGrouped;
  }, [checklistTasks, searchTerm]);

  // Get summary stats
  const getStats = () => {
    const totalTasks = checklistTasks.length;
    const overdueTasks = checklistTasks.filter(task => isTaskOverdue(task)).length;
    const uniqueDoers = Object.keys(groupedChecklistTasks).length;
    const completedTasks = checklistTasks.filter(task => {
      const taskInfo = getTaskInfo(task);
      return taskInfo.toLowerCase().includes('completed') || taskInfo.toLowerCase().includes('done');
    }).length;

    return {
      totalTasks,
      overdueTasks,
      uniqueDoers,
      completedTasks,
      overduePercentage: totalTasks > 0 ? ((overdueTasks / totalTasks) * 100).toFixed(1) : 0
    };
  };

  const stats = getStats();

  // Toggle doer expansion
  const toggleDoerExpansion = (doer) => {
    setExpandedDoers(prev => ({
      ...prev,
      [doer]: !prev[doer]
    }));
  };

  // Generate WhatsApp message for a doer's tasks
  const generateWhatsAppMessage = (doer, tasks) => {
    const taskList = tasks.map((task, index) => {
      const taskInfo = getTaskInfo(task);
      const deadline = getDeadline(task);
      const delay = getDelay(task);
      const isOverdue = isTaskOverdue(task);
      
      return `${index + 1}. ${taskInfo}${deadline ? `\n   📅 Deadline: ${deadline}` : ''}${isOverdue ? `\n   ⚠️ Status: Overdue (${delay})` : ''}`;
    }).join('\n\n');

    const overdueCount = tasks.filter(task => isTaskOverdue(task)).length;
    const overdueText = overdueCount > 0 ? `\n⚠️ ${overdueCount} task(s) are overdue` : '';

    return `Hi ${doer},

Here are your checklist tasks from PC Dashboard:

${taskList}

Total Tasks: ${tasks.length}${overdueText}

Please review and update the status as needed.

Best regards,
${currentUser.name}
Process Coordinator`;
  };

  // Share tasks via WhatsApp
  const shareViaWhatsApp = (doer, tasks) => {
    const message = generateWhatsAppMessage(doer, tasks);
    const encodedMessage = encodeURIComponent(message);
    const whatsappUrl = `https://wa.me/?text=${encodedMessage}`;
    window.open(whatsappUrl, '_blank');
  };

  if (loading) {
    return (
      <div className="k-page">
        <div className="k-card k-loading">
          <div className="k-loading-spinner" />
          <p>Loading checklist tasks…</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="k-page">
        <div className="k-card k-error" style={{ textAlign: 'center', padding: '2rem' }}>
          <AlertTriangle size={36} style={{ margin: '0 auto 12px', color: 'var(--status-error)' }} />
          <p style={{ fontWeight: 600, marginBottom: 8 }}>Error Loading Checklist</p>
          <p>{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="k-page">

      {/* Stats */}
      <div className="k-stats-grid">
        <div className="k-stat-card">
          <div className="k-stat-icon" style={{ background: 'var(--forest-100,#d4e8dd)' }}>
            <CheckSquare size={20} style={{ color: 'var(--forest-600,#2d6a50)' }} />
          </div>
          <div>
            <div className="k-stat-value">{stats.totalTasks}</div>
            <div className="k-stat-label">Total Tasks</div>
          </div>
        </div>
        <div className="k-stat-card">
          <div className="k-stat-icon" style={{ background: 'rgba(239,68,68,0.1)' }}>
            <AlertTriangle size={20} style={{ color: 'var(--status-error)' }} />
          </div>
          <div>
            <div className="k-stat-value" style={{ color: 'var(--status-error)' }}>{stats.overdueTasks}</div>
            <div className="k-stat-label">Overdue</div>
          </div>
        </div>
        <div className="k-stat-card">
          <div className="k-stat-icon" style={{ background: 'var(--forest-100,#d4e8dd)' }}>
            <Users size={20} style={{ color: 'var(--forest-500,#3d7a5f)' }} />
          </div>
          <div>
            <div className="k-stat-value">{stats.uniqueDoers}</div>
            <div className="k-stat-label">Assigned Doers</div>
          </div>
        </div>
        <div className="k-stat-card">
          <div className="k-stat-icon" style={{ background: 'rgba(139,92,246,0.1)' }}>
            <CheckCircle2 size={20} style={{ color: '#7c3aed' }} />
          </div>
          <div>
            <div className="k-stat-value" style={{ color: '#7c3aed' }}>
              {stats.totalTasks > 0 ? ((stats.completedTasks / stats.totalTasks) * 100).toFixed(1) : 0}%
            </div>
            <div className="k-stat-label">Completion Rate</div>
          </div>
        </div>
      </div>

      {/* Search */}
      <div ref={filterSentinelRef} aria-hidden="true" style={{ height: 1, marginBottom: -1 }} />
      <div className={`k-card p-4 k-filter-sticky ${isFilterStuck ? 'is-stuck' : ''}`}>
        <div className="k-filter-bar">
          <input
            type="text"
            className="k-search"
            placeholder="Search checklist tasks…"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            style={{ maxWidth: 360 }}
          />
        </div>
      </div>

      {/* Grouped by doer */}
      {Object.keys(groupedChecklistTasks).length === 0 ? (
        <div className="k-card k-empty">
          <CheckSquare size={36} style={{ marginBottom: 12, opacity: 0.4 }} />
          <p style={{ fontWeight: 600 }}>No Checklist Tasks Found</p>
          <p>{checklistTasks.length === 0 ? 'No checklist tasks available' : 'No tasks match your search'}</p>
        </div>
      ) : (
        Object.keys(groupedChecklistTasks).map(doer => {
          const doerTasks = groupedChecklistTasks[doer];
          const overdueCount = doerTasks.filter(task => isTaskOverdue(task)).length;
          const isExpanded = !!expandedDoers[doer];

          return (
            <div key={doer} className="k-card" style={{ padding: 0, overflow: 'hidden' }}>
              {/* Doer header */}
              <div
                style={{
                  display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                  padding: '14px 16px', cursor: 'pointer', gap: 12,
                  borderBottom: isExpanded ? '1px solid var(--border-subtle)' : 'none',
                  background: overdueCount > 0 ? 'rgba(239,68,68,0.04)' : 'var(--surface-1)'
                }}
                onClick={() => toggleDoerExpansion(doer)}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                  <div style={{
                    width: 38, height: 38, borderRadius: 8, flexShrink: 0,
                    background: overdueCount > 0 ? 'rgba(239,68,68,0.1)' : 'var(--forest-100,#d4e8dd)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center'
                  }}>
                    <User size={18} style={{ color: overdueCount > 0 ? 'var(--status-error)' : 'var(--forest-600,#2d6a50)' }} />
                  </div>
                  <div>
                    <p style={{ fontWeight: 600, fontSize: 14, color: 'var(--text-primary)', margin: 0 }}>{doer}</p>
                    <p style={{ fontSize: 12, color: 'var(--text-muted)', margin: 0 }}>
                      {doerTasks.length} task{doerTasks.length !== 1 ? 's' : ''}
                      {overdueCount > 0 && <span style={{ color: 'var(--status-error)', marginLeft: 6 }}>• {overdueCount} overdue</span>}
                    </p>
                  </div>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <button
                    className="k-btn-ghost"
                    style={{ padding: '4px 10px', fontSize: 12, display: 'flex', alignItems: 'center', gap: 4, background: 'var(--forest-600,#2d6a50)', color: '#fff', border: 'none' }}
                    onClick={(e) => { e.stopPropagation(); shareViaWhatsApp(doer, doerTasks); }}
                    title={`Share ${doer}'s tasks via WhatsApp`}
                  >
                    <MessageCircle size={13} />
                    <span>Share</span>
                  </button>
                  <span className={`k-badge ${overdueCount > 0 ? 'k-badge-overdue' : 'k-badge-role'}`}>
                    {doerTasks.length}
                  </span>
                  {isExpanded
                    ? <ChevronUp size={16} style={{ color: 'var(--text-muted)' }} />
                    : <ChevronDown size={16} style={{ color: 'var(--text-muted)' }} />
                  }
                </div>
              </div>

              {/* Task rows */}
              {isExpanded && (
                <div className="k-task-list">
                  {doerTasks.map((task, index) => {
                    const taskInfo = getTaskInfo(task);
                    const whatToDo = getWhatToDo(task);
                    const deadline = getDeadline(task);
                    const delay = getDelay(task);
                    const link = getLink(task);
                    const isOverdue = isTaskOverdue(task);

                    return (
                      <div
                        key={index}
                        className={`k-task-row${isOverdue ? ' overdue' : ''}`}
                        style={{ borderLeft: `3px solid ${isOverdue ? 'var(--status-error)' : 'var(--forest-400,#4a8c6e)'}` }}
                      >
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div className="k-task-title">
                            <CheckSquare size={14} style={{ color: isOverdue ? 'var(--status-error)' : 'var(--forest-500,#3d7a5f)', flexShrink: 0 }} />
                            <span className="k-badge k-badge-role">#{index + 1}</span>
                            {isOverdue && <span className="k-badge k-badge-overdue">Overdue</span>}
                            {taskInfo || whatToDo || 'No task description'}
                          </div>
                          <div className="k-task-meta">
                            {deadline && (
                              <span className="k-task-meta-item">
                                <Calendar size={11} /> {deadline}
                              </span>
                            )}
                            {isOverdue && delay && (
                              <span className="k-task-meta-item" style={{ color: 'var(--status-error)' }}>
                                <Clock size={11} /> Delay: {delay}
                              </span>
                            )}
                          </div>
                        </div>
                        {link && (
                          <a
                            href={link}
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{ color: 'var(--gold-500,#c8a03c)', flexShrink: 0 }}
                            title="Open task link"
                          >
                            <ExternalLink size={14} />
                          </a>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })
      )}
    </div>
  );
};

export default PCTasksChecklist;
